import { useState, useEffect } from "react";
import { Field, Label, Input } from "@headlessui/react";
import { isNullOrWhiteSpace } from "../../app/utils";
import { handleEnterKey } from "../../app/keyboardUtils";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import KeyValuePair from "../../interfaces/keyValuePair";
import MultiSelect from "./MultiSelect";
import { EditReviewGroupModel } from "./peerReviewGroupsApiSlice";

interface EditPeerReviewGroupFormProps {
	initialGroupData: EditReviewGroupModel;
	submitLabel: string;
	onSubmit: (model: EditReviewGroupModel) => string;
	onClose: () => void;
}

export function EditPeerReviewGroupForm(props: EditPeerReviewGroupFormProps) {
	const initializeSelectedStudents = () => {
		const selected = props.initialGroupData.writerPool
			.filter((s) => props.initialGroupData.writerIds.includes(s.id))
			.map((s) => ({ key: s.id, value: s.fullName }));
		return selected;
	};

	const [disableConfirm, setDisableConfirm] = useState(true);
	const [groupName, setGroupName] = useState(props.initialGroupData.name);
	const [selectedStudents, setSelectedStudents] = useState(initializeSelectedStudents());
	const [errorMessage, setErrorMessage] = useState("");

	const studentPool = props.initialGroupData.writerPool.map((x) => ({
		key: x.id,
		value: x.fullName,
	}));

	useEffect(() => {
		setDisableConfirm(!isFormValid());
	}, [groupName, selectedStudents]);

	function isFormValid() {
		if (isNullOrWhiteSpace(groupName)) {
			return false;
		}
		if (selectedStudents.length < 2) {
			return false;
		}
		return true;
	}

	function handleSelectedChange(selected: KeyValuePair<number, string>[]) {
		setSelectedStudents(selected);
	}

	function handleCancel() {
		props.onClose();
	}

	function handleSubmit() {
		const model: EditReviewGroupModel = {
			id: props.initialGroupData.id,
			name: groupName,
			writerIds: selectedStudents.map((w) => w.key),
			writerPool: [],
		};
		const error = props.onSubmit(model);
		setErrorMessage(error);
	}

	return (
		<div>
			{errorMessage && (
				<AlertMessage
					className="text-sm mt-4 py-2 !px-4"
					type={AlertLevel.Error}
					message={errorMessage}
					showIcon={true}
				/>
			)}
			<section>
				<Field>
					<Label
						htmlFor="groupName"
						className="block mt-4 font-semibold text-sm text-gray-900">
						Group Name
					</Label>
					<Input
						id="groupNameTextBox"
						name="groupName"
						type="text"
						value={groupName}
						className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						onChange={(e) => setGroupName(e.target.value)}
					/>
				</Field>
				<Field>
					<Label
						htmlFor="students"
						className="block mt-4 font-semibold text-sm text-gray-900">
						Students
					</Label>
					<MultiSelect
						pool={studentPool}
						selected={selectedStudents}
						className="w-full mt-1"
						defaultText="Select a student..."
						onSelectedChange={handleSelectedChange}
					/>
				</Field>
			</section>
			<div className="mt-4 grid grid-cols-2 gap-3">
				<button
					type="button"
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</button>
				<button
					type="button"
					id="confirm-button"
					disabled={disableConfirm}
					onClick={handleSubmit}
					onKeyDown={(e) => handleEnterKey(e, handleSubmit)}
					className="btn btn-primary">
					{props.submitLabel}
				</button>
			</div>
		</div>
	);
}
